import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { getListingsBySku } from '../../services/marketplaceapi';
import apiClient from '../../services/api';

// Helper function to get complete image URL
const getFullImageUrl = (imagePath) => {
  if (!imagePath) return null;
  
  // If it already starts with http or https, it's already a full URL
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Otherwise, prepend the API base URL
  // Remove leading slash if present to prevent double slashes
  const cleanPath = imagePath.startsWith('/') ? imagePath.substr(1) : imagePath;
  const fullUrl = `${apiClient.defaults.baseURL}/${cleanPath}`;
  
  // Debug log to help troubleshoot image loading issues
  console.log('Image path:', imagePath);
  console.log('API base URL:', apiClient.defaults.baseURL);
  console.log('Full image URL:', fullUrl);
  
  return fullUrl;
};

const MarketplaceListingsModal = ({ open, onClose, skuId, skuCode }) => {
  const [listings, setListings] = useState({
    amazon: [],
    flipkart: [],
    myntra: [],
    ajio: [],
    nykaa: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skuDetails, setSkuDetails] = useState(null);

  useEffect(() => {
    if (open && skuCode) {
      fetchListings(skuCode);
    }
  }, [open, skuCode]);

  const fetchListings = async (skuCode) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getListingsBySku(skuCode);
      
      // Log the response to help with debugging
      console.log('API Response:', response.data);
      
      setListings({
        amazon: response.data.listings.amazon || [],
        flipkart: response.data.listings.flipkart || [],
        myntra: response.data.listings.myntra || [],
        ajio: response.data.listings.ajio || [],
        nykaa: response.data.listings.nykaa || []
      });
      setSkuDetails(response.data.sku_details);
    } catch (err) {
      console.error('Error fetching marketplace listings:', err);
      setError('Failed to load marketplace listings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get total count of listings
  const getTotalListingsCount = () => {
    return listings.amazon.length + listings.flipkart.length + listings.myntra.length + listings.ajio.length + listings.nykaa.length;
  };

  // Helper function to render a marketplace section
  const renderMarketplaceSection = (title, data) => {
    if (data.length === 0) return null;

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
          <Chip label={title} color={getColorByMarketplace(title)} size="small" sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {data.length} listing{data.length !== 1 ? 's' : ''}
          </Typography>
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Marketplace</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="center">Website URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((listing, index) => (
                <TableRow key={index} hover>
                  <TableCell>{listing.marketplace}</TableCell>
                  <TableCell>{listing.company_name}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => window.open(listing.website_url, '_blank')}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  // Helper function to get color by marketplace
  const getColorByMarketplace = (marketplace) => {
    const colorMap = {
      'Amazon': 'warning',
      'Flipkart': 'primary',
      'Myntra': 'secondary',
      'Ajio': 'error',
      'Nykaa': 'success'
    };
    return colorMap[marketplace] || 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" component="div">
            Marketplace Listings
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            SKU: {skuCode || 'N/A'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : getTotalListingsCount() === 0 ? (
          <Alert severity="info">No marketplace listings found for this SKU.</Alert>
        ) : (
          <Box>
            {skuDetails && (
              <Box sx={{ mb: 3, display: 'flex', gap: 3 }}>
                {/* Product Image */}
                <Box sx={{ width: 120, height: 120, display: 'flex', justifyContent: 'center', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                  {skuDetails.image ? (
                    <img 
                      src={getFullImageUrl(skuDetails.image)} 
                      alt={skuDetails.product_name || 'Product'} 
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} 
                    />
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'text.disabled' }}>
                      <ImageNotSupportedIcon sx={{ fontSize: 40 }} />
                      <Typography variant="caption" sx={{ mt: 1 }}>No image</Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Product Details */}
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Product Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    SKU: {skuDetails.sku || 'N/A'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Name: {skuDetails.product_name || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            )}
            {renderMarketplaceSection('Amazon', listings.amazon)}
            {renderMarketplaceSection('Flipkart', listings.flipkart)}
            {renderMarketplaceSection('Myntra', listings.myntra)}
            {renderMarketplaceSection('Ajio', listings.ajio)}
            {renderMarketplaceSection('Nykaa', listings.nykaa)}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MarketplaceListingsModal;
